import _ from "lodash";
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useSelector } from "react-redux";
import { getHelpSections } from "../../../store/help/help.reducer";

const HighlightContext = createContext(null);

function* makeScrollWalker() {
  const allResults = document.querySelectorAll("mark");
  const resultsLength = allResults.length;

  if (resultsLength === 0) {
    return;
  }

  let currentIndex = 0;
  let previousIndex = 0;

  while (true) {
    allResults[previousIndex].classList.remove("active");
    allResults[currentIndex].classList.add("active");
    allResults[currentIndex].scrollIntoView({
      block: "start",
    });

    const offset = yield;

    previousIndex = currentIndex;
    currentIndex =
      (((currentIndex + offset) % resultsLength) + resultsLength) %
      resultsLength;
  }
}

const useGetActiveSection = (sections) => {
  const sectionsRef = useRef(null);
  const [activeSection, updateActiveSection] = useState(null);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections, sectionsRef]);

  const updateActiveSections = useCallback(
    (sectionId, status) => {
      let isChanged = false;

      _.forEach(sectionsRef.current, (section) => {
        if (section.id === sectionId) {
          section.isActive = status;
          isChanged = true;
        }

        if (isChanged) {
          const firstActiveSection = _.find(sectionsRef.current, {
            isActive: true,
          });

          if (firstActiveSection) {
            updateActiveSection(firstActiveSection.id);
          }
        }
      });
    },
    [sectionsRef]
  );

  return [activeSection, updateActiveSections];
};

export const withHighlightContext = (Component) => () => {
  const scrollWalkerRef = useRef(null);
  const [searchString, updateSearchString] = useState("");
  const [highlightedSections, updateHighlightedSections] = useState({});
  const sections = useSelector(getHelpSections);
  const [activeSection, updateActiveSections] = useGetActiveSection(sections);

  const amountHighlightedItems = _.reduce(
    highlightedSections,
    (result, value) => result + value,
    0
  );

  const onHighlightDone = useCallback(
    (id, amount) => {
      updateHighlightedSections((prevState) => ({
        ...prevState,
        [id]: amount,
      }));
    },
    [updateHighlightedSections]
  );

  useEffect(() => {
    scrollWalkerRef.current = makeScrollWalker();
    scrollWalkerRef.current.next(1);
  }, [highlightedSections, scrollWalkerRef]);

  const context = useMemo(
    () => ({
      onDoSearch: updateSearchString,
      amountHighlightedItems,
      searchString,
      updateActiveSections,
      activeSection,
      onHighlightDone,
      onGoToNext: () => scrollWalkerRef.current.next(1),
      onGoToPrevious: () => scrollWalkerRef.current.next(-1),
    }),
    [
      activeSection,
      searchString,
      amountHighlightedItems,
      updateActiveSections,
      scrollWalkerRef,
      onHighlightDone,
    ]
  );

  return (
    <HighlightContext.Provider value={context}>
      <Component />
    </HighlightContext.Provider>
  );
};

export const useHighlightContext = () => useContext(HighlightContext);
